import React, { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { foundation } from '../../../ui/tokens';
import { AppIcon } from '../../../ui/AppIcon';
import { resolveNavGlyph } from './navGlyphRegistry';
import './navGlyph.css';

const RESTING = foundation.color.neutral.textSecondary;
const NAVY = foundation.color.brand.navy;
const CORAL = foundation.color.brand.stop;
const PLAY_BACKUP_MS = 900;

/**
 * CrewVex-drawn nav glyph. Unmapped icons fall back to the lucide AppIcon.
 * Plays its move once when `active` flips false -> true AFTER mount (never on
 * first mount). Coral detail shows only while active.
 * `busy` swaps the glyph for a refresh spinner in the same box; when it clears
 * the glyph returns and plays its move (the hand-back). `replayKey` plays the
 * move once whenever it changes after mount (scrolled-down re-tap).
 */
export default function NavGlyph({ Icon, size = 18, active = false, strokeWidth = 1.75, busy = false, replayKey, style, ...rest }) {
  const def = resolveNavGlyph(Icon);
  const spanRef = useRef(null);
  const prevActive = useRef(active);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const prevBusy = useRef(busy);
  const prevReplayKey = useRef(replayKey);

  const stop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    timerRef.current = null;
    rafRef.current = null;
    if (spanRef.current) spanRef.current.classList.remove('is-playing');
  }, []);

  useLayoutEffect(() => {
    const el = spanRef.current;
    if (!def || !def.draw || !el) return;
    el.querySelectorAll('.cvx-ng-d').forEach((p) => {
      try {
        if (typeof p.getTotalLength === 'function') {
          p.style.setProperty('--len', String(Math.ceil(p.getTotalLength())));
        }
      } catch (e) { /* no geometry (jsdom) */ }
    });
  }, [def]);

  const play = useCallback(() => {
    stop();
    const el = spanRef.current;
    if (!el) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      el.classList.add('is-playing');
      timerRef.current = setTimeout(stop, PLAY_BACKUP_MS);
    });
  }, [stop]);

  useEffect(() => {
    const was = prevActive.current;
    prevActive.current = active;
    if (!def || !active || was) return;
    play();
  }, [active, def, play]);

  useEffect(() => {
    const was = prevBusy.current;
    prevBusy.current = busy;
    if (!def || busy || !was) return;
    play(); // hand-back
  }, [busy, def, play]);

  useEffect(() => {
    const was = prevReplayKey.current;
    prevReplayKey.current = replayKey;
    if (!def || was === replayKey) return;
    play();
  }, [replayKey, def, play]);

  useEffect(() => stop, [stop]);

  if (!def) return <AppIcon Icon={Icon} size={size} active={active} strokeWidth={strokeWidth} style={style} {...rest} />;

  return (
    <span
      ref={spanRef}
      className="cvx-navglyph"
      data-nav-glyph={def.id}
      data-active={active ? 'true' : 'false'}
      data-move={def.move}
      data-nav-glyph-busy={busy ? 'true' : 'false'}
      onAnimationEnd={(e) => { if (e.target === e.currentTarget) stop(); }}
      style={{ position: 'relative', width: size, height: size, color: active ? NAVY : RESTING, '--cvx-ng-coral': CORAL, ...style }}
      {...rest}
    >
      <svg
        className="cvx-ng-main"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-hidden="true"
        focusable="false"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {def.render()}
      </svg>
      <svg
        className="cvx-ng-spin"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        aria-hidden="true"
        focusable="false"
        fill="none"
      >
        <circle cx="12" cy="12" r="9" stroke={NAVY} strokeOpacity="0.18" strokeWidth="1.75" />
        <path d="M12 3 A9 9 0 0 1 21 12" stroke={NAVY} strokeWidth="1.75" strokeLinecap="round" />
        <circle cx="21" cy="12" r="1.6" fill="var(--cvx-ng-coral)" />
      </svg>
    </span>
  );
}
